import {
  ReactNode,
  createContext,
  useState,
  useContext,
  useCallback,
} from 'react';
import { AxiosResponse } from 'axios';
import jwtDecode, { JwtPayload } from 'jwt-decode';
import environmentConfig from '../config/environment-configs';
import { JwtUser, User } from '../types/user';
import { Subscriber } from '../types/subscriber';
import { HomeInfo } from '../types/homeInfo';
import { api } from '../services/api';

interface AuthContextData {
  authenticated: boolean;
  error: string;
  success: string;
  loading: boolean;
  user: User;
  subscription: Subscriber;
  homeInfo: HomeInfo;
  setError: React.Dispatch<React.SetStateAction<string>>;
  setSuccess: React.Dispatch<React.SetStateAction<string>>;
  authenticateOnAPI: () => Promise<string>;
  getValidToken: () => Promise<string>;
  privateRoute: () => void;
  logout: () => void;
  hideMobilePhoneIntervention: (hideIntervention: boolean) => void;
  hideCPFIntervention: (hideIntervention: boolean) => void;
}

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthContext = createContext<AuthContextData>(
  {} as AuthContextData,
);

export function verifyParamsURL(): string[] {
  const url = new URL(window.location.href);

  const queryParamState = url.searchParams.get('state');
  const queryParamCode = url.searchParams.get('code');

  const params: Array<string> = [];

  if (queryParamState && queryParamCode) {
    params.push(queryParamState);
    params.push(queryParamCode);
    return params;
  }

  return params;
}

export const setRedirectAndGetStateCodeParams = () => {
  const url = new URL(window.location.href);
  const queryParamRedirect = url.searchParams.get('redirect');

  if (queryParamRedirect) {
    localStorage.setItem('redirect', queryParamRedirect);
  }

  if (window.location.hash?.replace(/\/$/, '') === '#/familia') {
    localStorage.setItem('redirect', 'familia');
  }

  if (window.location.hash?.replace(/\/$/, '') === '#/email/change') {
    localStorage.setItem('redirect', 'email/change');
  }
};

export const modifyURL = (any: string) => {
  const url = new URL(any);

  const redirect = localStorage.getItem('redirect');
  if (redirect) {
    localStorage.removeItem('redirect');
    return `${url.origin + url.pathname}#/${redirect}`;
  }

  return url.origin + url.pathname;
};

export function AuthProvider({ children }: AuthProviderProps) {
  const [authenticated, setAuthenticated] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [homeInfo, setHomeInfo] = useState<HomeInfo>({
    componentConsent: {
      show: false,
      data: {},
      betaUser: false,
    },
    componentDelete: {
      show: false,
      data: {},
      betaUser: false,
    },
    componentAssociateAccount: {
      show: false,
      data: {
        google: false,
      },
      betaUser: false,
    },
    componentSession: {
      show: false,
      data: {},
      betaUser: false,
    },
    componentCommunicationPreference: {
      show: false,
      data: {},
      betaUser: false,
    },
    componentProviders: {
      show: false,
      data: [{}],
      betaUser: false,
    },
    componentLoyalty: {
      show: false,
      data: {},
      betaUser: false,
    },
    componentUnderage: {
      show: false,
      data: {},
      betaUser: false,
    },
  });
  const [subscription, setSubscription] = useState<Subscriber>({
    subscriber: '',
    status: '',
    statusId: '',
    docNumberLocked: false,
  });
  const [user, setUser] = useState<User>({
    globoId: '',
    fullName: '',
    email: '',
    phones: {
      cellPhone: '',
      cellPhoneFull: '',
      dddCellPhone: '',
      dddPhone: '',
      mobilePhoneConfirmed: false,
      phone: '',
      phoneFull: '',
    },
    docNumber: '',
    photoURL: '',
    birthDate: '',
    privacyPolicy: {
      version: 0,
      jurisdiction: '',
      url: '',
    },
  });

  function getState() {
    return new Promise(resolve => {
      const fn = () =>
        api
          .get('/auth/state', {
            headers: {
              'Content-Type': 'text/plain',
            },
          })
          .then(response => {
            if (response.status !== 200) {
              setTimeout(() => fn(), 1000);
            } else {
              resolve(response.data);
            }
          })
          .catch(() => {
            setTimeout(() => fn(), 1000);
          });
      return fn();
    });
  }

  const redirectToLoginPage = useCallback(async () => {
    const params = new URLSearchParams(document.location.search);
    const origemId = params.get('origemId');
    if (origemId) {
      await getState().then(state => {
        window.location.href = `${environmentConfig.REDIRECT_URL}&state=${state}&f_origemId=${origemId}`;
      });
    } else {
      await getState().then(state => {
        window.location.href = `${environmentConfig.REDIRECT_URL}&state=${state}`;
      });
    }
  }, []);

  function verifyJWTExpired(token: string) {
    try {
      const jwtDecoded = jwtDecode<JwtPayload>(token);
      const jwtExpirationTime = jwtDecoded.exp as number;
      const now = new Date().getTime() / 1000;

      return now >= jwtExpirationTime;
    } catch (err) {
      throw new Error('Erro na decodificação do Token');
    }
  }

  async function refreshToken(token: string) {
    const response = await api.post<string>('/auth/refresh', null, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response;
  }

  async function getSubscription(token: string) {
    try {
      await api
        .get('/user/subscriber', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then((response: AxiosResponse<Subscriber>) => {
          setSubscription(response.data);
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
          setError('Falha na requisição!');
        });
    } catch (err) {
      throw new Error('Erro na obtenção dos dados de inscrição do Usuário!');
    }
  }

  async function getInfoScreen(token: string) {
    try {
      await api
        .get('/home/info', {
          headers: {
            Authorization: `Bearer ${token}`,
            GLBID: document.cookie.replace(
              /(?:(?:^|.*;\s*)GLBID\s*=\s*([^;]*).*$)|^.*$/,
              '$1',
            ),
          },
        })
        .then((response: AxiosResponse<HomeInfo>) => {
          setHomeInfo(response.data);
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
          setError('Falha na requisição!');
        });
    } catch (err) {
      throw new Error('Erro na obtenção dos dados de inscrição do Usuário!');
    }
  }

  async function getUser(token: string) {
    try {
      await api
        .post(
          '/user',
          {},
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          },
        )
        .then((response: AxiosResponse<User>) => {
          setUser(response.data);
          setAuthenticated(true);
          setLoading(false);
        })
        .catch(() => {
          localStorage.removeItem('token_jwt');
          setAuthenticated(false);
          setLoading(false);
          setError('Falha na requisição!');
        });
    } catch (err) {
      throw new Error('Erro na obtenção dos dados do Usuário!');
    }
  }

  async function efetuarLogout(token: string) {
    await api
      .delete('/auth', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {
        localStorage.removeItem('token_jwt');
        // setAuthenticated(false);
      })
      .catch(() => {
        setError('Falha na requisição de Logout!');
      });
  }

  async function logout() {
    try {
      let token = localStorage.getItem('token_jwt') || '';

      if (token && verifyJWTExpired(token)) {
        const response = await refreshToken(token);

        if (response.status === 200) {
          token = response.data;
        }
      }

      await efetuarLogout(token);
      window.location.href = `${environmentConfig.LOGOUT_URL}/&client_id=${environmentConfig.OIDC_CLIENT_ID}`;
    } catch (err) {
      throw new Error('Erro na realização da requisição de Logout!');
    }
  }

  const authenticateOnAPI = useCallback(async (): Promise<string> => {
    const token = localStorage.getItem('token_jwt');

    if (token && !verifyJWTExpired(token)) {
      setAuthenticated(true);
    }

    if (token && verifyJWTExpired(token)) {
      const response = await refreshToken(token);
      if (response.status !== 200) {
        setAuthenticated(false);
        localStorage.removeItem('token_jwt');
        await redirectToLoginPage();
        return '';
      }

      localStorage.setItem('token_jwt', response.data);
      setAuthenticated(true);
    }

    return localStorage.getItem('token_jwt') || '';
  }, [redirectToLoginPage]);

  const getValidToken = useCallback(async (): Promise<string> => {
    const currentToken = localStorage.getItem('token_jwt');

    if (!currentToken) {
      return authenticateOnAPI();
    }

    if (verifyJWTExpired(currentToken)) {
      return authenticateOnAPI();
    }

    return currentToken;
  }, [authenticateOnAPI]);

  const privateRoute = useCallback(async () => {
    try {
      setLoading(true);
      setRedirectAndGetStateCodeParams();
      let token = await authenticateOnAPI();

      if (!token) {
        const paramsURL = verifyParamsURL();

        if (!paramsURL.length) {
          await redirectToLoginPage();
          return;
        }

        const response = await api.post('/auth/code', null, {
          headers: {
            'Content-Type': 'text/plain',
            'X-Auth-State': paramsURL[0],
            'X-Auth-Code': paramsURL[1],
          },
        });

        setAuthenticated(true);
        token = response.data;
        localStorage.setItem('token_jwt', response.data);
      }

      getUser(token);

      getSubscription(token);
      getInfoScreen(token);

      setLoading(false);
    } catch (err) {
      setLoading(false);
      setAuthenticated(false);
      localStorage.removeItem('token_jwt');
      await redirectToLoginPage();
    }
  }, [redirectToLoginPage, authenticateOnAPI]);

  function hideMobilePhoneIntervention(hideIntervention: boolean) {
    user.hideMobilePhoneIntervention = hideIntervention;
  }

  function hideCPFIntervention(hideIntervention: boolean) {
    user.hideCPFIntervention = hideIntervention;
  }

  return (
    <AuthContext.Provider
      value={{
        authenticated,
        setError,
        error,
        success,
        setSuccess,
        loading,
        user,
        subscription,
        homeInfo,
        hideMobilePhoneIntervention,
        hideCPFIntervention,
        authenticateOnAPI,
        getValidToken,
        privateRoute,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);

  return context;
}

export function getUserFromJwtToken() {
  const token = localStorage.getItem('token_jwt');
  if (token) {
    const data = jwtDecode<JwtUser>(token);
    if (data) {
      return data.user;
    }
  }
  return null;
}
